import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Mail, MessageCircle, Phone, Facebook, Instagram, Youtube } from "lucide-react";
import logo from "@/assets/logo.webp";
import trustBadge from "@/assets/trust-badge.webp";
import { NewsletterSignup } from "./NewsletterSignup";

export function SiteFooter() {
  const { data: settings } = useQuery({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data } = await supabase.from("site_settings").select("key,value");
      return Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
    },
  });

  const social = (url?: string, label?: string, Icon?: any) =>
    url ? (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        className="rounded-full bg-background/10 p-3 transition-colors hover:bg-background/20"
      >
        <Icon className="h-4 w-4" />
      </a>
    ) : null;

  return (
    <footer className="mt-20 border-t border-border bg-foreground text-background">
      <div className="container mx-auto grid gap-12 px-4 py-16 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="inline-flex items-center rounded-md bg-background px-3 py-2">
            <img src={logo} alt="Raise Sri Lanka" width={160} height={44} className="h-10 w-auto" />
          </div>
          <p className="mt-4 max-w-md text-sm text-background/70">
            A 100% volunteer-run nonprofit. We help Sri Lankan families with
            schooling, shelter, medical care and emergency relief — funded
            entirely by people like you.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <img
              src={trustBadge}
              alt="Raise Sri Lanka — registered 501(c)(3) nonprofit"
              width={72}
              height={72}
              className="h-16 w-16 flex-shrink-0"
            />
            <p className="text-xs text-background/60">
              Registered 501(c)(3) nonprofit.<br />
              Donations are tax-deductible in the US.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {social(settings?.whatsapp_url, "WhatsApp", Phone)}
            {social(settings?.messenger_url, "Messenger", MessageCircle)}
            {social(settings?.facebook_url, "Facebook", Facebook)}
            {social(settings?.instagram_url, "Instagram", Instagram)}
            {social(settings?.youtube_url, "YouTube", Youtube)}
            {settings?.contact_email && (
              <a
                href={`mailto:${settings.contact_email}`}
                aria-label="Email"
                className="rounded-full bg-background/10 p-3 transition-colors hover:bg-background/20"
              >
                <Mail className="h-4 w-4" />
              </a>
            )}
          </div>

          <div className="mt-8 max-w-sm">
            <NewsletterSignup />
          </div>
        </div>

        <div>
          <h2 className="font-display text-lg">Get involved</h2>
          <ul className="mt-4 space-y-2 text-sm text-background/70">
            <li><Link to="/donate" className="hover:text-background">Donate</Link></li>
            <li><Link to="/the-circle" className="hover:text-background">The Circle (monthly)</Link></li>
            <li><Link to="/sponsor" className="hover:text-background">Sponsor a project</Link></li>
            <li><Link to="/get-involved" className="hover:text-background">Get involved</Link></li>
            <li><Link to="/events" className="hover:text-background">Events</Link></li>
            <li><Link to="/fundraisers" className="hover:text-background">Browse fundraisers</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-lg">Organization</h2>
          <ul className="mt-4 space-y-2 text-sm text-background/70">
            <li><Link to="/about" className="hover:text-background">About</Link></li>
            <li><Link to="/news" className="hover:text-background">Blogs</Link></li>
            <li><Link to="/newsletters" className="hover:text-background">Newsletters</Link></li>
            <li><Link to="/help" className="hover:text-background">Help & FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-background">Contact</Link></li>
            <li><Link to="/transparency" className="hover:text-background">Transparency</Link></li>
            <li><Link to="/map" className="hover:text-background">Project map</Link></li>
            <li><Link to="/donor-wall" className="hover:text-background">Donor wall</Link></li>
            <li><Link to="/birthday" className="hover:text-background">Pledge your birthday</Link></li>
            <li><Link to="/impact-squad" className="hover:text-background">Little Helpers</Link></li>
            <li><Link to="/partners" className="hover:text-background">Corporate partners</Link></li>
            <li><Link to="/volunteer" className="hover:text-background">Volunteer with us</Link></li>
          </ul>
        </div>
      </div>

        <div className="border-t border-background/10">
        <div className="container mx-auto flex flex-col items-center justify-between gap-2 px-4 py-6 text-xs text-background/80 md:flex-row">
          <span>© {new Date().getFullYear()} Raise Sri Lanka. All rights reserved.</span>
          <a
            href="https://idealounge.com.au"
            target="_blank"
            rel="noreferrer"
            className="transition-colors hover:text-background"
          >
            Technology partner — Idea lounge
          </a>
          <span className="flex items-center gap-3">
            <Link
              to="/ai-context"
              className="rounded-full border border-background/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-background/70 transition-colors hover:border-background/40 hover:text-background"
              title="Machine-readable knowledge graph for AI agents and answer engines"
            >
              AI-ready ✦
            </Link>
            <span>A volunteer non-profit organization.</span>
          </span>
        </div>
      </div>
    </footer>
  );
}
